import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AcceptInvitation from "@/pages/auth/AcceptInvitation";
import Dashboard from "@/pages/Dashboard";
import Sas from "@/pages/Sas";
import DocumentQueue from "@/pages/DocumentQueue";
import Docs from "@/pages/Docs";
import DocumentTypes from "@/pages/DocumentTypes";
import Contacts from "@/pages/Contacts";
import Settings from "@/pages/Settings";
import Shares from "@/pages/Shares";
import PublicShare from "@/pages/PublicShare";
import PublicBundle from "@/pages/PublicBundle";
import ServerLogs from "@/pages/superadmin/ServerLogs";

function Protected({ children, adminOnly, superAdminOnly }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">
        Chargement…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin" && user.role !== "super_admin")
    return <Navigate to="/" replace />;
  if (superAdminOnly && user.role !== "super_admin")
    return <Navigate to="/" replace />;

  // Protection de route pour les non-admins ayant des restrictions
  if (user.role !== "admin" && user.role !== "super_admin" && Array.isArray(user.allowed_routes)) {
    const seg = "/" + (location.pathname.replace(/^\//, "").split("/")[0] || "");
    const allowed = user.allowed_routes.includes(seg) || user.allowed_routes.includes(location.pathname);
    if (!allowed && location.pathname !== "/") {
      return <Navigate to="/" replace />;
    }
  }

  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />

          <Route
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="sas" element={<Sas />} />
            <Route path="queue" element={<DocumentQueue />} />
            <Route path="docs" element={<Docs />} />
            <Route path="types" element={<DocumentTypes />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="partages" element={<Shares />} />
            <Route
              path="parametres"
              element={
                <Protected adminOnly>
                  <Settings />
                </Protected>
              }
            />
            {/* Backward-compatible redirects */}
            <Route path="settings" element={<Navigate to="/parametres" replace />} />
            <Route path="admin/users" element={<Navigate to="/parametres?tab=users" replace />} />
            <Route path="admin/types" element={<Navigate to="/parametres?tab=types" replace />} />
            <Route
              path="administration/logs"
              element={
                <Protected superAdminOnly>
                  <ServerLogs />
                </Protected>
              }
            />
          </Route>

          {/* Route publique — hors layout authentifié */}
          <Route path="/share/:token" element={<PublicShare />} />
          <Route path="/bundle/:token" element={<PublicBundle />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
